import { PhotoModel } from './protocols';
import { Attachment } from './attachments';
import { ProjectNote, ProjectChecklist } from './projects';
import { AssigneeMeta, DelegationMeta } from './assignees';

// Re-exported for discoverability next to the WorkOrder type. Canonical
// definitions and the getAssignees/getDelegations read-shim live in ./assignees.
export { getAssignees, getDelegations, isUnassigned } from './assignees';
export type { AssigneeMeta, DelegationMeta } from './assignees';

export enum WorkOrderPriority {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high'
}

/** Bootstrap color variants for work order priorities */
export const workOrderPriorityColors: Record<WorkOrderPriority, string> = {
    [WorkOrderPriority.HIGH]: 'danger',
    [WorkOrderPriority.MEDIUM]: 'warning',
    [WorkOrderPriority.LOW]: 'secondary'
};

/** Swedish labels for work order priorities */
export const workOrderPriorityLabels: Record<WorkOrderPriority, string> = {
    [WorkOrderPriority.HIGH]: 'Hög',
    [WorkOrderPriority.MEDIUM]: 'Medium',
    [WorkOrderPriority.LOW]: 'Låg'
};

export enum WorkOrderStatus {
    OPEN = 'open',
    COMPLETED = 'completed',
    CANCELLED = 'cancelled'
}

export interface WorkOrder {
    key: string;
    buildingKey: string;

    // Core fields
    title: string;
    description?: string;
    priority: WorkOrderPriority;
    dueDate?: string;  // YYYY-MM-DD hh:mm:ss
    status: WorkOrderStatus;

    // Linked components. Use getComponentKeys() when reading.
    componentKeys?: string[];

    // Link to project (optional, same building)
    projectKey?: string;

    // Link to a lägenhet/lokal (optional, same building; units plan phase 3).
    // `unitLabel` is a display snapshot ("Lägenhet 1203") like leases carry.
    unitKey?: string;
    unitLabel?: string;

    // Assignment — multi-assignee model. Read via getAssignees()/getDelegations()
    // (they fold the legacy scalars below into map form). Writers emit only the
    // maps and delete the legacy scalars.
    assignees?: { [internalUid: string]: AssigneeMeta };          // internal users
    delegations?: { [externalUserId: string]: DelegationMeta };   // external delegates (the grant; each reaches it via their personal link)

    // Legacy single-assignee scalars — READ-ONLY via the normalizers, never
    // written going forward. Kept for the read-shim until the one-time backfill.
    /** @deprecated read via getAssignees(); folded into `assignees` */
    assignedTo?: string;  // UID for internal user
    /** @deprecated read via getDelegations(); folded into `delegations` */
    externalUserId?: string;   // References users/userList/{uid}
    /** @deprecated read via getDelegations(); folded into `delegations` */
    guestTokenKey?: string;    // Reference to guest access token

    // Transient opt-in flag written alongside newly-added assignees to request
    // the assignment email (the "skicka e-post" checkbox). Consumed and cleared
    // server-side by workOrderAssignmentNotification; not meant to persist.
    sendAssignmentEmail?: boolean;

    // Requirements (set by admin)
    requiresPhoto: boolean;
    requiresComment: boolean;
    requiresConditionUpdate: boolean;  // Only enabled if at least one component is linked

    // Completion data (filled by user)
    completionPhotos?: { [key: string]: PhotoModel };
    completionComment?: string;

    // File attachments (receipts, documents, images)
    // Can be added/removed at any time, even after completion/archival
    attachments?: { [key: string]: Attachment };

    // Photos from source issue (read-only, set during conversion from issue)
    photos?: { [key: string]: PhotoModel };

    // Granular section toggles (admin-enabled per work order; absent key = off).
    // Each section is independently toggleable and available on any work order.
    enabledSections?: {
        notes?: boolean;
        checklists?: boolean;
        beforePictures?: boolean;
        progressPictures?: boolean;
        afterPictures?: boolean;
    };

    // Granular section data — mirrors the ProjectModel sub-structures.
    // afterPictures also holds completion photos: completion evidence and the
    // "after" photos are the same field (no separate completionPhotos going forward).
    beforePictures?: { [key: string]: PhotoModel };
    progressPictures?: { [key: string]: PhotoModel };
    afterPictures?: { [key: string]: PhotoModel };
    notes?: { [key: string]: ProjectNote };
    checklists?: { [key: string]: ProjectChecklist };

    // Audit
    createdBy: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;
    completedBy?: string;
    completedAt?: string;

    // Archive metadata (set when moved to archive)
    archivedYear?: number;

    // Source reference (when created from issue conversion)
    sourceIssueKey?: string;

    // Source reference (when created from maintenance activity)
    maintenanceTemplateId?: string;

    /**
     * Component of the source maintenance activity, pinned at creation. Immutable
     * — unlike the user-editable componentKeys, this never changes, so the
     * reschedule-on-completion trigger always advances the correct activity.
     * Absent on legacy work orders created before this field existed.
     */
    maintenanceComponentKey?: string;

    // Actual cost tracking (set at completion, optional)
    /**
     * Actual cost in SEK (whole numbers). Set at completion time.
     */
    actualCost?: number;

    /**
     * Percentage of actualCost that is investment (vs operating expense).
     * Only meaningful when actualCost is set. Stored as 0-100 integer.
     * Defaults to 0 when actualCost is provided but percentage is not.
     */
    actualCostInvestmentPercentage?: number;

    /**
     * Condition per linked component as entered at completion (internal or
     * guest). Written by the completion functions; read by the Slutförande
     * summary. Absent when the work order did not require a condition update.
     */
    completionConditions?: {
        [componentKey: string]: { value: number; comment?: string };
    };

    /**
     * Reminders to the external delegates (molnfast-react#1613). `schedule` is
     * admin-written (detail row, or guestTokenApi.create on delegation); `sent`
     * is written by the functions job only. Read through workOrderReminders.ts.
     */
    reminders?: WorkOrderReminders;
}

/**
 * Absent or `off` = no reminders; a field left out = that reminder is off.
 * `off` is an admin's explicit "none", which a delegation never overwrites.
 */
export interface WorkOrderReminderSchedule {
    /** Turned off by an admin; then neither number is stored */
    off?: true;
    /** 0–30: one nudge on dueDate − daysBefore */
    daysBefore?: number;
    /** 1–30: overdue reminder every N days after the day-after notice (absent = only that notice) */
    repeatEveryDays?: number;
    updatedBy: string; // UID
    updatedAt: string; // YYYY-MM-DD hh:mm:ss
}

export type WorkOrderReminderKind = 'before' | 'overdue' | 'adminNotice';

/** One round sent by the job (write-once). */
export interface WorkOrderReminderLogEntry {
    kind: WorkOrderReminderKind;
    date: string; // YYYY-MM-DD hh:mm:ss
    dueDate: string; // YYYY-MM-DD
    recipients?: string[]; // UIDs (delegates; delegating admins for adminNotice)
    skipped?: string[]; // UIDs not reached
}

export interface WorkOrderReminders {
    schedule?: WorkOrderReminderSchedule;
    sent?: { [pushKey: string]: WorkOrderReminderLogEntry };
}

// SYNC: WorkOrder type must match mf-functions/functions/src/workOrders/types.ts

/**
 * Normalized list of linked component keys. Tolerates object-shaped arrays
 * from RTDB and dedupes/drops empty entries.
 */
export function getComponentKeys(
    workOrder: Pick<WorkOrder, 'componentKeys'>
): string[] {
    const raw = workOrder.componentKeys;
    if (!raw) {
        return [];
    }
    const values = Array.isArray(raw) ? raw : Object.values(raw);
    const keys = values.filter(
        (key): key is string => typeof key === 'string' && key.length > 0
    );
    return [...new Set(keys)];
}

// For list views with building name resolved
export interface WorkOrderWithBuilding extends WorkOrder {
    buildingName: string;
}

export interface ConditionInput {
    value: number;
    comment?: string;
}

// Data required to complete a work order
export interface CompletionData {
    photos?: { [key: string]: PhotoModel };
    comment?: string;
    /**
     * Per-component conditions, keyed by componentKey. Required for every
     * linked component when the work order has requiresConditionUpdate.
     */
    newConditions?: { [componentKey: string]: ConditionInput };
    // Actual cost tracking (optional)
    actualCost?: number;
    actualCostInvestmentPercentage?: number;
}
